// Köken rozeti — Geçmiş Rapor Aktarımı (Task 15, tasarım review 14A).
//
// 14A: yüksek güvenli aktarılmış yanıtın TEK göstergesi. Nötr pill (uyarı değil —
// "bu satır aktarıldı" bilgisidir), soru etiketinin sağındaki sol grupta
// `etiket → Zorunlu → s.136 → aktarımdan sonra düzenlendi` sırasında durur.
// `projects.import_report` izni varsa raporu yeni sekmede açan `<a>`, yoksa `<span>`
// (metin her iki durumda da kalır — 7A). Alıntı görsel olarak GİZLİ bir span'de
// durur ve `aria-describedby` ile rozete bağlanır: ekran okuyucu kaynağı duyar,
// göz gürültü görmez. G16 — rozet asla `<label>` İÇİNE girmez (a11y: label'ın
// tıklama hedefi input'tur, içine link konursa tıklama çakışır).

import { useId } from "react";
import type { ImportSummary, ResponseProvenance } from "@mahir/shared";
import { fillTemplate } from "../../lib/period-ui";
import {
  IMPORT_COPY,
  fileOpenUrl,
  provenanceRefLabel,
  scaledSuffixText,
} from "../../lib/report-import-ui";

export interface ProvenanceBadgeProps {
  entry: ResponseProvenance;
  /** Girdinin ait olduğu aktarım (`imports.find(i => i.id === entry.importId)`). */
  importSummary: ImportSummary | null;
  /** `projects.import_report` — yoksa link yerine düz metin (7A). */
  canOpenReport: boolean;
}

/** 14A stil sözleşmesi — nötr pill; `--color-warning`/`--color-error` KULLANILMAZ. */
const badgeStyle = {
  padding: "1px 8px",
  borderRadius: 999,
  fontSize: "0.68rem",
  fontWeight: 600,
  background: "var(--color-bg)",
  color: "var(--color-text-2)",
  border: "1px solid var(--color-border)",
  whiteSpace: "nowrap",
  textDecoration: "none",
  display: "inline-block",
} as const;

const visuallyHidden = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
} as const;

export function ProvenanceBadge(props: ProvenanceBadgeProps): JSX.Element | null {
  const { entry, importSummary, canOpenReport } = props;
  const snippetId = useId();
  const ref = provenanceRefLabel(entry);
  if (!ref) return null;

  const href =
    canOpenReport && importSummary
      ? fileOpenUrl(importSummary.fileUrl, importSummary.fileKind, entry.sourcePages)
      : null;
  // DOCX'te "sayfa" demek yanlış olur — locator şablonu kullanılır.
  const ariaTemplate =
    ref.kind === "page"
      ? href
        ? IMPORT_COPY.badgeAriaOpen
        : IMPORT_COPY.badgeAria
      : href
        ? IMPORT_COPY.badgeAriaLocatorOpen
        : IMPORT_COPY.badgeAriaLocator;
  const ariaLabel = fillTemplate(ariaTemplate, { page: ref.aria, locator: ref.aria });
  // 10A — rozet "aktarım anındaki kaynak"tır; title dosya adını da taşır (eng review:
  // her girdi kendi `importId`'sine bağlı, çoklu aktarımda hangi dosya olduğu görünür).
  const baseTitle = fillTemplate(IMPORT_COPY.badgeTitle, { ref: ref.label });
  const title = importSummary ? `${baseTitle} · ${importSummary.fileName}` : baseTitle;
  // P8(c) — değer sorunun karakter sınırına indirildiyse rozet bunu söyler; aksi halde
  // kullanıcı kısaltmayı hiçbir yerde göremiyordu (Grup B incelemesi, 4. madde).
  // P15/1b — ölçeklenen değerde rozet "×1.000.000 ölçeklendi" der (kırpma ile aynı
  // desen); çarpan köken satırından gelir (inceleme kararı 2).
  const methodSuffix =
    entry.method === "ai-import-trim"
      ? IMPORT_COPY.trimmedSuffix
      : entry.method === "ai-import-scaled"
        ? scaledSuffixText(entry.scaleFactor)
        : null;
  const label = methodSuffix === null ? ref.label : `${ref.label} · ${methodSuffix}`;

  return (
    <>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={ariaLabel}
          aria-describedby={snippetId}
          title={title}
          style={badgeStyle}
        >
          {label}
        </a>
      ) : (
        <span aria-label={ariaLabel} aria-describedby={snippetId} title={title} style={badgeStyle}>
          {label}
        </span>
      )}
      <span id={snippetId} style={visuallyHidden}>
        {entry.snippet}
      </span>
    </>
  );
}
